import { useState, useEffect, useCallback } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { getCurrentUser, User, OrgAssignment } from '../../../services/userService';

interface AdvisorAccess {
  customerIds: string[];
  isAdmin: boolean;
  isLoading: boolean;
  canAccessCustomerId: (customerId: string) => boolean;
  hasAccess: boolean;
  hasSystemAccess: boolean; // Has Auth0 dashboard-access role
  needsSetup: boolean; // Has Auth0 role but not in MongoDB
  isDashboardAdmin: boolean;
  isDashboardAdvisor: boolean;
  azureGroups: string[];
  role?: string;
  permissions?: User['permissions'];
  user?: User | null;
  // Enterprise org scopes (firm/branch/team). Non-planner users with at least
  // one assignment get the enterprise view.
  orgAssignments: OrgAssignment[];
  hasEnterpriseView: boolean;
  // Re-fetch the DB user record (e.g. after a self-service profile update).
  refreshUser: () => Promise<void>;
}

export const useAdvisorAccess = (): AdvisorAccess => {
  const { user: auth0User, isLoading: authLoading, getAccessTokenSilently, loginWithRedirect } = useAuth0();
  const [dbUser, setDbUser] = useState<User | null | undefined>(undefined);
  const [isLoadingUser, setIsLoadingUser] = useState(true);

  // Fetch user from database (also exposed as refreshUser for self-service
  // profile updates — the header/greeting read the DB user, not token claims)
  const fetchUser = useCallback(async () => {
    if (!auth0User?.sub) {
      setDbUser(null);
      setIsLoadingUser(false);
      return;
    }

    try {
      // Get Auth0 access token (audience already configured in auth0Config)
      const token = await getAccessTokenSilently();

      const userData = await getCurrentUser(token);
      setDbUser(userData);
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.error('Error fetching user from database:', error);
      }

      // Check if it's a refresh token error
      const errorMessage = error instanceof Error ? error.message : String(error);
      if (errorMessage.includes('Missing Refresh Token') || errorMessage.includes('refresh_token')) {
        console.log('Refresh token error detected, redirecting to login...');
        loginWithRedirect({
          appState: { returnTo: window.location.pathname }
        });
        return;
      }

      setDbUser(null);
    } finally {
      setIsLoadingUser(false);
    }
  }, [auth0User?.sub, getAccessTokenSilently, loginWithRedirect]);

  useEffect(() => {
    if (!authLoading) {
      fetchUser();
    }
  }, [authLoading, fetchUser]);

  // Compute access based on Auth0 roles + database user
  const computeAccess = (): Omit<AdvisorAccess, 'isLoading' | 'canAccessCustomerId' | 'refreshUser'> => {
    if (!auth0User || !auth0User.email) {
      return {
        customerIds: [],
        isAdmin: false,
        hasAccess: false,
        hasSystemAccess: false,
        needsSetup: false,
        isDashboardAdmin: false,
        isDashboardAdvisor: false,
        azureGroups: [],
        role: undefined,
        permissions: undefined,
        user: undefined,
        orgAssignments: [],
        hasEnterpriseView: false
      };
    }

    // Check for Auth0 dashboard-access role
    const namespace = 'https://planscout.com';
    const auth0Roles = (auth0User as any)[`${namespace}/roles`] || [];
    const hasSystemAccess = auth0Roles.includes('dashboard-access');

    if (process.env.NODE_ENV === 'development') {
      console.log('Auth0 system roles:', auth0Roles);
      console.log('Has dashboard-access:', hasSystemAccess);
    }

    // If no Auth0 system access, deny immediately
    if (!hasSystemAccess) {
      if (process.env.NODE_ENV === 'development') {
        console.log('Access denied: No Auth0 dashboard-access role');
      }
      return {
        customerIds: [],
        isAdmin: false,
        hasAccess: false,
        hasSystemAccess: false,
        needsSetup: false,
        isDashboardAdmin: false,
        isDashboardAdvisor: false,
        azureGroups: [],
        role: undefined,
        permissions: undefined,
        user: null,
        orgAssignments: [],
        hasEnterpriseView: false
      };
    }

    // Has Auth0 access - now check database
    if (dbUser) {
      const isSuperAdmin = dbUser.role === 'superadmin';
      const isDashboardAdmin = dbUser.role === 'dashboard-admin' || isSuperAdmin;
      const isDashboardAdvisor = dbUser.role === 'dashboard-advisor';
      const orgAssignments = Array.isArray(dbUser.orgAssignments) ? dbUser.orgAssignments : [];
      const hasEnterpriseView = orgAssignments.length > 0 && dbUser.role !== 'planner';

      if (process.env.NODE_ENV === 'development') {
        console.log('User access from database:', {
          email: dbUser.email,
          role: dbUser.role,
          isDashboardAdmin,
          isDashboardAdvisor,
          customerIdCount: dbUser.assignedCustomerIds.length,
          permissions: dbUser.permissions
        });
      }

      return {
        customerIds: dbUser.assignedCustomerIds,
        isAdmin: isSuperAdmin || isDashboardAdmin,
        hasAccess: true,
        hasSystemAccess: true,
        needsSetup: false,
        isDashboardAdmin,
        isDashboardAdvisor,
        azureGroups: [],
        role: dbUser.role,
        permissions: dbUser.permissions,
        user: dbUser,
        orgAssignments,
        hasEnterpriseView
      };
    }

    // Has Auth0 access but NOT in database = needs setup
    if (process.env.NODE_ENV === 'development') {
      console.log('User has Auth0 access but not in database - setup required');
    }

    return {
      customerIds: [],
      isAdmin: false,
      hasAccess: false,
      hasSystemAccess: true,
      needsSetup: true,
      isDashboardAdmin: false,
      isDashboardAdvisor: false,
      azureGroups: [],
      role: undefined,
      permissions: undefined,
      user: null,
      orgAssignments: [],
      hasEnterpriseView: false
    };
  };

  const access = computeAccess();

  const canAccessCustomerId = (customerId: string): boolean => {
    if (!access.hasAccess) return false;

    // Superadmin and dashboard-admin can access all
    if (access.isAdmin) return true;

    // Check if user has access to this specific customer ID
    return access.customerIds.includes(customerId);
  };

  return {
    ...access,
    isLoading: authLoading || isLoadingUser,
    canAccessCustomerId,
    refreshUser: fetchUser
  };
};